import React, { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, Trash, X } from "@phosphor-icons/react";
import { savePhotoBlob, removePhotoBlob, getPhotoBlob, newId } from "../lib/db";
import { fileUrl } from "../lib/api";

function PhotoThumb({ photo, onRemove, onCaption }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url;
    let active = true;
    (async () => {
      if (photo.localId) {
        const blob = await getPhotoBlob(photo.localId);
        if (blob && active) {
          url = URL.createObjectURL(blob);
          setSrc(url);
          return;
        }
      }
      if (photo.fileId && active) setSrc(fileUrl(photo.fileId));
    })();
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo]);

  return (
    <div className="relative rounded-xl overflow-hidden border-2 border-line bg-white" data-testid="photo-thumb">
      {src ? (
        <img src={src} alt={photo.caption || "site photo"} className="w-full h-40 object-cover" />
      ) : (
        <div className="w-full h-40 bg-paper flex items-center justify-center text-steel">
          <ImageIcon size={32} />
        </div>
      )}
      <button
        type="button"
        data-testid="photo-remove-btn"
        onClick={onRemove}
        className="absolute top-2 right-2 w-9 h-9 rounded-full bg-navydark/80 text-white flex items-center justify-center"
        aria-label="Remove photo"
      >
        <Trash size={18} weight="bold" />
      </button>
      <input
        className="w-full h-11 px-3 text-sm border-t-2 border-line bg-white text-navydark focus:outline-none"
        placeholder="Add caption…"
        value={photo.caption || ""}
        data-testid="photo-caption-input"
        onChange={(e) => onCaption(e.target.value)}
      />
      {!photo.uploaded && (
        <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-warning text-white px-2 py-0.5 rounded-full">
          Not uploaded
        </span>
      )}
    </div>
  );
}

export default function PhotoUpload({ photos = [], onChange }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  async function addFiles(fileList) {
    const additions = [];
    for (const file of Array.from(fileList)) {
      const localId = newId();
      await savePhotoBlob(localId, file);
      additions.push({
        localId,
        filename: file.name || `photo-${localId}.jpg`,
        caption: "",
        category: "",
        uploaded: false,
      });
    }
    onChange([...(photos || []), ...additions]);
  }

  async function remove(idx) {
    const ph = photos[idx];
    if (ph.localId) await removePhotoBlob(ph.localId);
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  }

  function setCaption(idx, text) {
    const next = photos.map((p, i) => (i === idx ? { ...p, caption: text } : p));
    onChange(next);
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <button
          type="button"
          data-testid="take-photo-btn"
          onClick={() => cameraRef.current?.click()}
          className="uc-btn uc-btn-teal h-24 flex-col !rounded-xl border-2 border-teal"
        >
          <Camera size={30} weight="bold" />
          <span className="text-sm">Take Photo</span>
        </button>
        <button
          type="button"
          data-testid="upload-photo-btn"
          onClick={() => galleryRef.current?.click()}
          className="uc-btn uc-btn-ghost h-24 flex-col !rounded-xl border-dashed"
        >
          <ImageIcon size={30} weight="bold" />
          <span className="text-sm">Upload</span>
        </button>
      </div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files && addFiles(e.target.files)}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && addFiles(e.target.files)}
      />
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p, i) => (
            <PhotoThumb
              key={p.localId || p.fileId || i}
              photo={p}
              onRemove={() => remove(i)}
              onCaption={(t) => setCaption(i, t)}
            />
          ))}
        </div>
      )}
      {photos.length === 0 && (
        <p className="text-sm text-steel">No photos yet. Photos captured offline upload automatically when back online.</p>
      )}
    </div>
  );
}
